import React, { ChangeEvent, useEffect, useState, useRef } from "react";
import { isNil } from "ramda";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { Container, Grid, Paper, Box, Typography, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import Layout from "../../Layout";
import { Data } from "../../../store/terminal/types";
import { getById } from "../../../store/terminal/queries";
import TerminalEditForm from "../terminalEditForm/TerminalEditForm";
import PageHeading from "../../../common/pageHeading/PageHeading";
import ProgressIndicator from "../../../common/progressIndicator/ProgressIndicator";
import DataLoadingIndicator from "../../../common/loadingIndicator/DataLoadingIndicator";
import useSession from "../../../effects/useSession";
import useDataFetching from "../../../effects/useDataFetching";
import pageStyles from "../../pageStyles";
import { fetchRequest as fetchTerminalDetail } from "../../../store/terminal/actions";
import { Tabs, Tab } from "@mui/material";
import TerminalCommands from "../terminalCommands/terminalCommands";
import { canUseTerminalCommands } from "../terminalCommands/TerminalCommandsHelper";
import { fetchFirmware } from "../../../store/myAxis/actions";
import { get } from "lodash";

interface LocationStateType {
  terminal?: Data;
  terminals?: Data[];
}

const CREATE = "CREATE";
const UPDATE = "UPDATE";
const BULK = "BULK";

export default function TerminalEdit(): React.ReactElement {
  const location = useLocation();
  const classes = pageStyles();
  const { id } = useParams<{ id: string }>();
  let mode = CREATE;

  if (id) {
    mode = UPDATE;
  } else {
    if (location.pathname === "/terminal/updategroup") {
      mode = BULK;
    }
  }

  const dispatch = useDispatch();
  const session = useSession();

  const [tabIndex, setTabIndex] = React.useState(0);

  const terminalFromState = (): Data => {
    if (mode === CREATE) {
      return (location.state as LocationStateType).terminal as Data;
    }

    // mode === BULK, return the first terminal of the group
    return ((location.state as LocationStateType).terminals as Data[])[0];
  };

  const terminal = useSelector(
    id ? getById(id) : (): Data => terminalFromState()
  ) as Data;

  const [showError, setShowError] = useState("");
  
  // Use ref to prevent multiple terminal detail fetches
  const hasFetchedTerminalDetail = useRef(false);
  const hasFetchedFirmware = useRef(false);
  const [terminalData, setTerminalData] = useState<any>(null);
  const [isTerminalLoading, setIsTerminalLoading] = useState(false);
  const [isFirmwareLoading, setIsFirmwareLoading] = useState(false);

  // Use the new data fetching hook
  const {
    isLoading: isDataLoading,
    isComplete: isDataComplete,
    individualLoading,
    firmwareData
  } = useDataFetching();

  const headingText = (): string => {
    switch (mode) {
      case UPDATE:
        return "Edit Terminal";
      case BULK:
        return "Edit Terminals Group";
      case CREATE:
      default:
        return "New Terminal";
    }
  };

  const lastLogin =
    id && terminal?.attributes.LastLogin
      ? dayjs(terminal.attributes.LastLogin).format("DD/MM/YYYY hh:mm:ss a")
      : "";

  // Effect to fetch terminal detail
  useEffect(() => {
    if (id && !hasFetchedTerminalDetail.current) {
      hasFetchedTerminalDetail.current = true;
      setIsTerminalLoading(true);
      
      dispatch(
        fetchTerminalDetail(id, {}, (err, data) => {
          setIsTerminalLoading(false);
          const terminalDataFromResponse = get(data, "data[0].attributes", {});
          if (terminalDataFromResponse) {
            setTerminalData(terminalDataFromResponse);
          }
        })
      );
    }
  }, [id, dispatch]);

  // Effect to fetch firmware when both terminal data and firmware data are available
  useEffect(() => {
    if (terminalData && firmwareData && !hasFetchedFirmware.current) {
      const { Manufacturer, Model } = terminalData;
      if (Model && firmwareData[Model] && !Array.isArray(firmwareData[Model])) {
        hasFetchedFirmware.current = true;
        setIsFirmwareLoading(true);
        dispatch(
          fetchFirmware({
            variant: (Manufacturer || "").toUpperCase(),
            model: (Model || "").toUpperCase(),
          })
        );
        // Set loading to false immediately since Redux actions don't return promises
        setIsFirmwareLoading(false);
      }
    }
  }, [terminalData, firmwareData, dispatch]);

  function a11yProps(index: number) {
    return {
      id: `vertical-tab-${index}`,
      "aria-controls": `vertical-tabpanel-${index}`,
    };
  }

  const handleChange = (event: ChangeEvent<{}>, newValue: number) => {
    setTabIndex(newValue);
  };

  // Show loading indicator while data is being fetched
  if (isDataLoading || !isDataComplete) {
    return (
      <Layout sessionData={session}>
        <main className={classes.content}>
          <div className={classes.appBarSpacer} />
          <Container maxWidth="lg" className={classes.container}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={12}>
                <Paper>
                  <PageHeading heading={headingText()} entity="Terminal" />
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper className={classes.paper}>
                  <Box p={3}>
                    <DataLoadingIndicator
                      individualLoading={individualLoading}
                      isComplete={isDataComplete}
                      isLoading={isDataLoading}
                    />
                    
                    {/* Terminal-specific loading states */}
                    {(isTerminalLoading || isFirmwareLoading) && (
                      <Box mt={3} pt={3} borderTop={1} borderColor="divider">
                        <Typography variant="h6" gutterBottom>
                          Terminal-Specific Data
                        </Typography>
                        <Box display="flex" gap={2}>
                          <Box display="flex" alignItems="center" gap={1}>
                              <CircularProgress size={16} />
                              <Typography variant="body2">Loading terminal details...</Typography>
                            </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </main>
      </Layout>
    );
  }

  return (
    <Layout sessionData={session}>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <Container maxWidth="lg" className={classes.container}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <Paper>
                <PageHeading heading={headingText()} entity="Terminal" />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex">
                <Box mr={2}>
                  <Typography
                    variant="caption"
                    style={{ visibility: !!lastLogin ? "visible" : "hidden" }}
                  >
                    Last Login: <strong>{lastLogin}</strong>
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              {canUseTerminalCommands(session) && (
                <Tabs
                  value={tabIndex}
                  onChange={handleChange}
                  orientation="horizontal"
                  aria-label="Terminal Edit"
                >
                  <Tab label="Edit" {...a11yProps(0)} />
                  <Tab label="Commands" {...a11yProps(1)} />
                </Tabs>
              )}

              {tabIndex == 0 && (
                <Paper className={classes.paper}>
                  {!isNil(id) && ( isNil(terminalData) || isNil(terminal)  || isTerminalLoading) ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                      <ProgressIndicator />
                      <Box ml={2}>
                        <Typography variant="body2" color="textSecondary">
                          Loading terminal details...
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <TerminalEditForm
                      terminal={terminal}
                      bulkTerminalIds={
                        mode === BULK
                          ? (
                              (location.state as LocationStateType)
                                .terminals as Data[]
                            ).map(({ id }) => id as string)
                          : []
                      }
                    />
                  )}
                </Paper>
              )}

              {tabIndex === 1 && (
                <Paper className={classes.paper}>
                  {!isNil(id) && (isNil(terminal) || isTerminalLoading) ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                      <ProgressIndicator />
                      <Box ml={2}>
                        <Typography variant="body2" color="textSecondary">
                          Loading terminal details...
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <TerminalCommands
                      terminal={terminal}
                      onError={(err: string): void => setShowError(err)}
                    />
                  )}
                </Paper>
              )}
            </Grid>
          </Grid>
        </Container>
      </main>
    </Layout>
  );
}
